import { useEffect, useState } from "react";
import { TrendingUp, AlertTriangle, Shield } from "lucide-react";
import AdminShell, { Panel, StatusPill } from "@/components/admin/AdminShell";
import AdminPager from "@/components/admin/AdminPager";
import KpiCard from "@/components/admin/KpiCard";
import DataTable from "@/components/admin/DataTable";
import { api } from "@/lib/api";
import { asAdminList, useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

export default function AdminFutures() {
  const [positions, setPositions] = useState([]);
  const [liquidations, setLiquidations] = useState([]);
  const [fund, setFund] = useState(null);
  const posPager = useAdminPager();
  const liqPager = useAdminPager();

  const load = async () => {
    try {
      const [p, l, f] = await Promise.all([
        api.get("/api/admin/futures/positions", { params: { status: "open", skip: posPager.skip, limit: posPager.limit } }),
        api.get("/api/admin/futures/liquidations", { params: { skip: liqPager.skip, limit: liqPager.limit } }),
        api.get("/api/admin/futures/insurance-fund"),
      ]);
      const pos = unwrapAdminList(p.data, "positions");
      setPositions(asAdminList(pos.items));
      posPager.setTotal(pos.total);
      const liq = unwrapAdminList(l.data);
      setLiquidations(asAdminList(liq.items));
      liqPager.setTotal(liq.total);
      setFund(f.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posPager.page, liqPager.page]);

  const positionRows = asAdminList(positions);
  const atRisk = positionRows.filter((p) => p.margin_ratio_pct != null && p.margin_ratio_pct <= 120);

  return (
    <AdminShell title="Futures & Liquidations">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard label="Open Positions" value={posPager.total} icon={TrendingUp} variant="blue" />
        <KpiCard label="At-risk (≤120%)" value={atRisk.length} icon={AlertTriangle} variant="red" />
        <KpiCard label="Recent Liquidations" value={liqPager.total} icon={AlertTriangle} variant="yellow" />
        <KpiCard
          label="Insurance Fund"
          value={fund ? `${fund.balance} ${fund.asset}` : "—"}
          icon={Shield}
          variant="green"
        />
      </div>

      <Panel className="mb-5">
        <div className="p-4 border-b border-[#1E2845]">
          <h3 className="text-[14px] font-bold">Open futures positions</h3>
        </div>
        <DataTable
          columns={[
            { key: "user", header: "User", render: (p) => <span className="text-[11px]">{p.user_id}</span> },
            { key: "pair", header: "Pair", render: (p) => p.pair },
            { key: "side", header: "Side", render: (p) => p.side },
            { key: "lev", header: "Lev", render: (p) => `${p.leverage}x` },
            {
              key: "mr",
              header: "Margin %",
              render: (p) => (
                <span className={p.margin_ratio_pct <= 100 ? "text-red-400 font-bold" : ""}>
                  {p.margin_ratio_pct?.toFixed(1)}%
                </span>
              ),
            },
            { key: "upnl", header: "uPnL", render: (p) => p.unrealized_pnl },
            { key: "mode", header: "Mode", render: (p) => p.margin_mode },
          ]}
          rows={positionRows}
          rowKey={(p) => p.position_id}
          empty="No open futures positions."
        />
        <AdminPager page={posPager.page} total={posPager.total} pageSize={posPager.pageSize} onPage={posPager.setPage} testId="futures-positions-pager" />
      </Panel>

      <Panel>
        <div className="p-4 border-b border-[#1E2845]">
          <h3 className="text-[14px] font-bold">Liquidation events</h3>
        </div>
        <DataTable
          columns={[
            { key: "id", header: "Event", render: (e) => e.event_id },
            { key: "pair", header: "Pair", render: (e) => e.pair },
            { key: "type", header: "Type", render: (e) => e.event_type },
            { key: "qty", header: "Qty", render: (e) => e.qty_closed },
            { key: "pnl", header: "PnL", render: (e) => e.realized_pnl },
            { key: "at", header: "At", render: (e) => new Date(e.created_at).toLocaleString() },
          ]}
          rows={liquidations}
          rowKey={(e) => e.event_id}
          empty="No liquidation events yet."
        />
        <AdminPager page={liqPager.page} total={liqPager.total} pageSize={liqPager.pageSize} onPage={liqPager.setPage} testId="futures-liq-pager" />
      </Panel>
    </AdminShell>
  );
}
